import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { updatePhotoInAllCaches } from '../utils/photoCache';
import { useAuthStore } from '../store/authStore';
import AudioRecorder from './AudioRecorder';
import CommentReactions from './CommentReactions';
import MentionInput from './MentionInput';
import MentionText from './MentionText';
import SharePhotoModal from './SharePhotoModal';

interface Photo {
  photoId: string;
  imageUrl: string;
  thumbnailUrls?: {
    small: string;
    medium: string;
    large: string;
  };
  ownerId: string;
  ownerName: string;
  likeCount: number;
  userHasLiked?: boolean;
  commentCount?: number;
  aspectRatio?: number;
  createdAt: string;
}

interface PhotoDetailModalProps {
  photo: Photo;
  onClose: () => void;
}

type AudioMessage = {
  messageId: string;
  authorId: string;
  authorName: string;
  audioUrl: string;
  durationSeconds: number;
  createdAt: string;
};

type Tag = {
  tag: string;
  type: 'person' | 'place' | 'thing';
  addedBy: string;
  createdAt: string;
};

type Comment = {
  commentId: string;
  authorId: string;
  authorName: string;
  authorEmail: string;
  content: string;
  createdAt: string;
};

export default function PhotoDetailModal({ photo, onClose }: PhotoDetailModalProps) {
  const queryClient = useQueryClient();
  const [isLiked, setIsLiked] = useState(photo.userHasLiked || false);
  const [likeCount, setLikeCount] = useState(photo.likeCount);
  const [commentCount, setCommentCount] = useState(photo.commentCount ?? 0);
  const [playingAudioId, setPlayingAudioId] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showLikesTooltip, setShowLikesTooltip] = useState(false);
  const [showAudioTooltip, setShowAudioTooltip] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [pendingDeleteCommentId, setPendingDeleteCommentId] = useState<string | null>(null);
  const { user, isSuperAdmin } = useAuthStore();
  
  // Tag adding state
  const [newTag, setNewTag] = useState('');
  const [tagType, setTagType] = useState<'person' | 'place' | 'thing'>('person');
  const [showAddTag, setShowAddTag] = useState(false);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  
  // Audio recording state
  const [showAudioRecorder, setShowAudioRecorder] = useState(false);
  
  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  
  // Delete confirmation state
  const [deleteConfirmMessageId, setDeleteConfirmMessageId] = useState<string | null>(null);

  const { data: detailedPhoto } = useQuery<Photo>({
    queryKey: ['photo-detail', photo.photoId],
    queryFn: () => api.getPhoto(photo.photoId),
    refetchInterval: 20 * 1000, // Poll every 20 seconds for like count updates
    staleTime: 0,
    initialData: photo,
  });

  const lastImageUrlRef = useRef<string | undefined>(
    detailedPhoto?.thumbnailUrls?.large || detailedPhoto?.imageUrl || photo.imageUrl
  );

  useEffect(() => {
    if (detailedPhoto) {
      setIsLiked(detailedPhoto.userHasLiked || false);
      setLikeCount(detailedPhoto.likeCount);
      const nextImageUrl = detailedPhoto.thumbnailUrls?.large || detailedPhoto.imageUrl;
      if (nextImageUrl && nextImageUrl !== lastImageUrlRef.current) {
        setImageLoaded(false);
        lastImageUrlRef.current = nextImageUrl;
      }
    }
  }, [detailedPhoto]);

  const currentPhoto = detailedPhoto ?? photo;

  // Fetch all data
  const { data: likesData } = useQuery({
    queryKey: ['photo-likes', photo.photoId],
    queryFn: () => api.listLikes(photo.photoId),
    enabled: likeCount > 0,
    refetchInterval: 15 * 1000, // Poll every 15 seconds when likes exist
  });

  const { data: messagesData } = useQuery({
    queryKey: ['messages', photo.photoId],
    queryFn: () => api.listMessages(photo.photoId),
    refetchInterval: 20 * 1000, // Poll every 20 seconds for new audio messages
  });

  const { data: tagsData } = useQuery({
    queryKey: ['tags', photo.photoId],
    queryFn: () => api.listTags(photo.photoId),
    refetchInterval: 30 * 1000, // Poll every 30 seconds for new tags
  });

  // Fetch all existing tags for autocomplete
  const { data: allTagsData } = useQuery({
    queryKey: ['all-tags'],
    queryFn: () => api.listAllTags(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Fetch active users for @mentions
  const { data: activeUsersData } = useQuery({
    queryKey: ['activeUsers'],
    queryFn: () => api.listActiveUsers(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const {
    data: commentsData,
    isLoading: areCommentsLoading,
  } = useQuery({
    queryKey: ['comments', photo.photoId],
    queryFn: () => api.listComments(photo.photoId),
    refetchInterval: 10 * 1000, // Poll every 10 seconds for new comments (most important)
  });

  const likes = likesData?.likes || [];
  const messages: AudioMessage[] = messagesData?.messages || [];
  const tags: Tag[] = tagsData?.tags || [];
  const comments: Comment[] = commentsData?.comments || [];
  const existingTags: Array<{tag: string; type: string; count: number}> = allTagsData?.tags || [];
  const activeUsers = activeUsersData?.users || [];
  
  // Fetch suggestions for @mentions (filter by query)
  const fetchMentionSuggestions = async (query: string) => {
    const lowerQuery = query.toLowerCase();
    const filtered = activeUsers
      .filter((u: { name: string; email: string; userId: string }) => 
        u.name.toLowerCase().includes(lowerQuery) || 
        u.email.toLowerCase().includes(lowerQuery)
      )
      .filter((u: { userId: string }) => u.userId !== user?.userId); // Don't suggest self
    
    const results = filtered.map((u: { userId: string; name: string }) => ({ id: u.userId, display: u.name }));
    return results;
  };
  
  // Get unique authors for audio messages (one per person)
  const audioMessageAuthors = messages.reduce<Array<{ authorId: string; authorName: string }>>(
    (acc, message: AudioMessage) => {
      if (!acc.find((m) => m.authorId === message.authorId)) {
        acc.push({
          authorId: message.authorId,
          authorName: message.authorName,
        });
      }
      return acc;
    },
    []
  );
  
  const audioMessageCount = audioMessageAuthors.length;

  useEffect(() => {
    const nextCount = comments.length;
    if (nextCount !== commentCount) {
      setCommentCount(nextCount);
      updatePhotoInAllCaches(queryClient, photo.photoId, (existing) => ({
        ...existing,
        commentCount: nextCount,
      }));
    }
  }, [comments.length, commentCount, queryClient, photo.photoId]);

  const likeMutation = useMutation({
    mutationFn: () =>
      isLiked ? api.unlikePhoto(photo.photoId) : api.likePhoto(photo.photoId),
    onSuccess: () => {
      const newIsLiked = !isLiked;
      const newLikeCount = Math.max(0, newIsLiked ? likeCount + 1 : likeCount - 1);
      setIsLiked(newIsLiked);
      setLikeCount(newLikeCount);
      queryClient.invalidateQueries({ queryKey: ['photo-likes', photo.photoId] });
      updatePhotoInAllCaches(queryClient, photo.photoId, (existing) => ({
        ...existing,
        userHasLiked: newIsLiked,
        likeCount: newLikeCount,
      }));
    },
  });

  const deleteAudioMutation = useMutation({
    mutationFn: (messageId: string) => api.deleteMessage(photo.photoId, messageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', photo.photoId] });
    },
  });

  // Autocomplete handlers
  const updateTagSuggestions = (value: string, type: 'person' | 'place' | 'thing') => {
    const searchValue = value.toLowerCase().trim();
    
    if (searchValue.length > 0) {
      const matchingTags = existingTags
        .filter((t) => {
          const tagLower = t.tag.toLowerCase();
          return t.type === type && 
            tagLower.includes(searchValue) &&
            tagLower !== searchValue;
        })
        .map((t) => t.tag)
        .slice(0, 8);
      
      setTagSuggestions(matchingTags);
      setShowTagSuggestions(matchingTags.length > 0);
    } else {
      const popularTags = existingTags
        .filter((t) => t.type === type)
        .sort((a, b) => b.count - a.count)
        .map((t) => t.tag)
        .slice(0, 8);
      
      setTagSuggestions(popularTags);
      setShowTagSuggestions(popularTags.length > 0);
    }
  };

  const handleTagChange = (value: string) => {
    setNewTag(value);
    setSelectedSuggestionIndex(-1);
    updateTagSuggestions(value, tagType);
  };

  const handleTagTypeChange = (type: 'person' | 'place' | 'thing') => {
    setTagType(type);
    setSelectedSuggestionIndex(-1);
    updateTagSuggestions(newTag, type);
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showTagSuggestions) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev < tagSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      
      case 'Enter':
        if (selectedSuggestionIndex >= 0) {
          e.preventDefault();
          selectSuggestion(tagSuggestions[selectedSuggestionIndex]);
        }
        break;
      
      case 'Escape':
        setShowTagSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
    }
  };

  const selectSuggestion = (tag: string) => {
    setNewTag(tag);
    setShowTagSuggestions(false);
    setSelectedSuggestionIndex(-1);
  };

  const addTagMutation = useMutation({
    mutationFn: () => {
      const normalizedTag = newTag.trim().toLowerCase();
      return api.addTag(photo.photoId, { tag: normalizedTag, type: tagType });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags', photo.photoId] });
      queryClient.invalidateQueries({ queryKey: ['all-tags'] });
      setNewTag('');
      setShowAddTag(false);
    },
  });

  const removeTagMutation = useMutation({
    mutationFn: (tag: string) => api.removeTag(photo.photoId, tag),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags', photo.photoId] });
    },
  });

  const addCommentMutation = useMutation({
    mutationFn: (content: string) => api.addComment(photo.photoId, { content }),
    onSuccess: () => {
      const nextCount = commentCount + 1;
      setCommentCount(nextCount);
      setNewComment('');
      updatePhotoInAllCaches(queryClient, photo.photoId, (existing) => ({
        ...existing,
        commentCount: nextCount,
      }));
      queryClient.invalidateQueries({ queryKey: ['comments', photo.photoId] });
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: string) => api.deleteComment(photo.photoId, commentId),
    onSuccess: () => {
      const nextCount = Math.max(0, commentCount - 1);
      setCommentCount(nextCount);
      updatePhotoInAllCaches(queryClient, photo.photoId, (existing) => ({
        ...existing,
        commentCount: nextCount,
      }));
      queryClient.invalidateQueries({ queryKey: ['comments', photo.photoId] });
    },
  });

  const handleLike = () => {
    likeMutation.mutate();
  };

  const handleAudioPlay = (messageId: string) => {
    setPlayingAudioId(playingAudioId === messageId ? null : messageId);
  };

  const handleAddTag = (e: React.FormEvent) => {
    e.preventDefault();
    if (newTag.trim()) {
      addTagMutation.mutate();
    }
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 md:p-8 bg-black/80 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-6xl max-h-[95vh] bg-white rounded-lg sm:rounded-xl md:rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors shadow-lg"
          aria-label="Close"
        >
          <svg className="w-6 h-6 text-gray-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="flex flex-col md:flex-row h-full max-h-[95vh] overflow-hidden">
          {/* Image Section */}
          <div className="relative flex-shrink-0 bg-black flex items-center justify-center md:flex-1">
            <img
              src={currentPhoto.thumbnailUrls?.large || currentPhoto.imageUrl}
              alt=""
              className={`w-full md:w-auto md:max-w-full md:max-h-[95vh] h-auto object-contain transition-opacity duration-300 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              onLoad={() => setImageLoaded(true)}
              onError={() => {
                setImageLoaded(false);
                queryClient.invalidateQueries({ queryKey: ['photo-detail', photo.photoId] });
              }}
            />
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-2 border-white/30 border-t-white"></div>
              </div>
            )}

            {/* Like and Audio icons overlay - bottom right */}
            {imageLoaded && (
              <div className="absolute bottom-3 right-3 flex items-center gap-2">
                {/* Like Button */}
                <div 
                  className="relative"
                  onMouseEnter={() => likeCount > 0 && setShowLikesTooltip(true)}
                  onMouseLeave={() => setShowLikesTooltip(false)}
                >
                  <button
                    onClick={handleLike}
                    disabled={likeMutation.isPending}
                    className="flex items-center gap-1.5 bg-black/60 backdrop-blur-sm px-2 py-1.5 rounded-md border border-white/10 shadow-lg hover:bg-black/70 transition-colors"
                  >
                    <svg
                      className={`w-4 h-4 transition-transform ${isLiked ? 'fill-red-500 text-red-500' : 'text-white'}`}
                      fill={isLiked ? 'currentColor' : 'none'}
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={isLiked ? 0 : 2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                      />
                    </svg>
                    {likeCount > 0 && (
                      <span className="text-xs font-semibold text-white">{likeCount}</span>
                    )}
                  </button>

                  {/* Likes Tooltip */}
                  {showLikesTooltip && likeCount > 0 && (
                    <div className="absolute bottom-full right-0 mb-2 w-56 sm:w-64 bg-white rounded-lg shadow-xl border border-gray-200 p-3 z-50 animate-slide-down">
                      <div className="text-xs font-semibold text-gray-700 mb-2">
                        Liked by {likeCount} {likeCount === 1 ? 'person' : 'people'}
                      </div>
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {likes.map((like: { userId: string; name: string; email: string }) => (
                          <div
                            key={like.userId}
                            className="text-sm text-gray-600 truncate"
                            title={like.email}
                          >
                            {like.name}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Share Button */}
                <div className="relative">
                  <button
                    onClick={() => setShowShareModal(true)}
                    className="flex items-center gap-1.5 bg-black/60 backdrop-blur-sm px-2 py-1.5 rounded-md border border-white/10 shadow-lg hover:bg-black/70 transition-colors"
                    title="Share photo"
                  >
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                      />
                    </svg>
                  </button>
                </div>

                {/* Audio Messages Count */}
                {audioMessageCount > 0 && (
                  <div 
                    className="relative"
                    onMouseEnter={() => setShowAudioTooltip(true)}
                    onMouseLeave={() => setShowAudioTooltip(false)}
                  >
                    <div className="flex items-center gap-1.5 bg-black/60 backdrop-blur-sm px-2 py-1.5 rounded-md border border-white/10 shadow-lg">
                      <svg
                        className="w-4 h-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                        />
                      </svg>
                      <span className="text-xs font-semibold text-white">{audioMessageCount}</span>
                    </div>

                    {/* Audio Tooltip */}
                    {showAudioTooltip && audioMessageCount > 0 && (
                      <div className="absolute bottom-full right-0 mb-2 w-56 sm:w-64 bg-white rounded-lg shadow-xl border border-gray-200 p-3 z-50 animate-slide-down">
                        <div className="text-xs font-semibold text-gray-700 mb-2">
                          Audio messages from {audioMessageCount} {audioMessageCount === 1 ? 'person' : 'people'}
                        </div>
                        <div className="space-y-1 max-h-48 overflow-y-auto">
                          {audioMessageAuthors.map((author) => (
                            <div
                              key={author.authorId}
                              className="text-xs text-gray-600 truncate"
                            >
                              {author.authorName}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Details Section */}
          <div className="w-full md:w-96 flex flex-col bg-white overflow-y-auto scrollbar-hide flex-1">

            {/* Tags Section - MOVED TO TOP */}
            <div className="px-3 sm:px-6 pt-5 sm:pt-6 pb-4 border-b border-gray-100">
              <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3">
                🏷️ Tags
              </h3>
              
              {/* Add Tag Section */}
              <div className="space-y-3 mb-3">
                {!showAddTag ? (
                  <button
                    onClick={() => setShowAddTag(true)}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 rounded-lg transition-colors border border-gray-200"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    <span>Add Tag</span>
                  </button>
                ) : (
                  <div className="space-y-3 p-4 bg-gray-50 rounded-lg border-2 border-primary-200">
                    <div className="text-sm font-semibold text-gray-900">Add New Tag</div>
                    <form onSubmit={handleAddTag} className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                          Tag Type
                        </label>
                        <div className="grid grid-cols-3 gap-3">
                          {(['person', 'place', 'thing'] as const).map((type) => (
                            <button
                              key={type}
                              type="button"
                              onClick={() => handleTagTypeChange(type)}
                              className={`px-3 py-3 font-bold rounded-lg transition-all transform ${
                                tagType === type
                                  ? 'bg-blue-600 border-2 border-blue-700 text-white shadow-lg scale-105 ring-4 ring-blue-200'
                                  : 'bg-white border-2 border-gray-300 text-gray-800 hover:border-blue-400 hover:bg-gray-50 hover:scale-102'
                              }`}
                            >
                              <div className={`text-2xl mb-1 ${tagType === type ? 'filter brightness-125' : ''}`}>
                                {type === 'person' ? '👤' : type === 'place' ? '📍' : '🎯'}
                              </div>
                              <div className={`capitalize ${tagType === type ? 'text-white text-sm font-extrabold' : 'text-gray-700 text-sm'}`}>
                                {type}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                          Tag Name
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={newTag}
                            onChange={(e) => handleTagChange(e.target.value)}
                            onKeyDown={handleTagKeyDown}
                            onFocus={() => {
                              if (newTag.length === 0) {
                                updateTagSuggestions('', tagType);
                              }
                            }}
                            onBlur={() => {
                              setTimeout(() => setShowTagSuggestions(false), 200);
                            }}
                            placeholder="e.g., Summer, Paris, Birthday..."
                            className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
                            autoComplete="off"
                            autoFocus
                          />
                          
                          {/* Tag Suggestions Dropdown */}
                          {showTagSuggestions && (
                            <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-48 overflow-y-auto z-[60]">
                              {tagSuggestions.length === 0 ? (
                                <div className="px-4 py-2 text-sm text-gray-500">
                                  No matching tags
                                </div>
                              ) : (
                                tagSuggestions.map((tag, index) => {
                                  const tagData = existingTags.find((t) => t.tag === tag);
                                  const usageCount = tagData?.count || 0;
                                  
                                  return (
                                    <button
                                      key={tag}
                                      type="button"
                                      onClick={() => selectSuggestion(tag)}
                                      className={`w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors flex items-center justify-between ${
                                        index === selectedSuggestionIndex ? 'bg-gray-100' : ''
                                      }`}
                                    >
                                      <span className="text-sm font-medium">{tag}</span>
                                      <span className="text-xs text-gray-400">
                                        {usageCount} photo{usageCount !== 1 ? 's' : ''}
                                      </span>
                                    </button>
                                  );
                                })
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 pt-1">
                        <button
                          type="submit"
                          disabled={!newTag.trim() || addTagMutation.isPending}
                          className="btn-primary flex-1 justify-center py-2 text-sm disabled:opacity-50"
                        >
                          {addTagMutation.isPending ? 'Adding...' : '✓ Add Tag'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowAddTag(false);
                            setNewTag('');
                            addTagMutation.reset();
                          }}
                          className="btn-ghost px-4 py-2.5 text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>

              {/* Display Tags */}
              {tags.length > 0 ? (
                <div className="space-y-2">
                  {(['person', 'place', 'thing'] as const).map((type) => {
                    const typeTags = tags.filter((t) => t.type === type);
                    if (typeTags.length === 0) return null;
                    
                    const icon = type === 'person' ? '👤' : type === 'place' ? '📍' : '🎯';
                    
                    return (
                      <div key={type} className="space-y-1.5">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          {icon} {type}s
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {typeTags.map((tag: Tag) => (
                            <span
                              key={`${tag.tag}-${tag.type}`}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 text-gray-700 border border-gray-200"
                            >
                              {tag.tag}
                              <button
                                onClick={() => removeTagMutation.mutate(tag.tag)}
                                disabled={removeTagMutation.isPending}
                                className="text-gray-400 hover:text-red-600 transition-colors ml-0.5"
                                title="Remove tag"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-gray-400">No tags yet</p>
              )}
            </div>

            {/* Comments Section */}
            <div className="px-3 sm:px-6 py-4 border-b border-gray-100">
              <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3">
                💬 Comments {commentCount > 0 && `(${commentCount})`}
              </h3>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const trimmed = newComment.trim();
                  if (!trimmed) return;
                  addCommentMutation.mutate(trimmed);
                }}
                className="space-y-2 mb-3"
              >
                <MentionInput
                  value={newComment}
                  onChange={(value) => setNewComment(value)}
                  fetchSuggestions={fetchMentionSuggestions}
                  placeholder="Add a comment... Type @ to mention someone 😊"
                  maxLength={2000}
                  rows={3}
                  className="text-sm"
                />
                
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="submit"
                    disabled={addCommentMutation.isPending || newComment.trim().length === 0}
                    className="btn-primary px-3 py-1.5 text-xs disabled:opacity-50 whitespace-nowrap"
                  >
                    {addCommentMutation.isPending ? 'Posting...' : 'Post Comment'}
                  </button>
                </div>
              </form>

              {areCommentsLoading ? (
                <div className="text-xs text-gray-400 py-2">Loading...</div>
              ) : comments.length === 0 ? (
                <p className="text-xs text-gray-400 italic py-1">No comments yet</p>
              ) : (
                <div className="space-y-2.5">
                  {comments.map((comment) => {
                    const canDeleteComment = isSuperAdmin || comment.authorId === user?.userId;
                    return (
                      <div
                        key={comment.commentId}
                        className="p-2.5 rounded-lg bg-gray-50 border border-gray-200"
                      >
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-gray-800 truncate">
                              {comment.authorName}
                            </p>
                            <p className="text-[10px] text-gray-400">
                              {new Date(comment.createdAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                              })}
                            </p>
                          </div>
                          {canDeleteComment && (
                            <button
                              onClick={() => setPendingDeleteCommentId(comment.commentId)}
                              className="text-gray-400 hover:text-red-600 transition-colors flex-shrink-0"
                              title="Delete comment"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
                          <MentionText
                            value={comment.content}
                            onMentionClick={() => {
                              // Future: Navigate to user profile or filter by user
                            }}
                          />
                        </p>
                        {/* Comment Reactions */}
                        <CommentReactions photoId={photo.photoId} commentId={comment.commentId} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Audio Messages Section */}
            <div className="px-3 sm:px-6 py-4 border-b border-gray-100">
              <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3">
                🎤 Audio Messages
              </h3>
              
              {/* Audio Recorder */}
              {showAudioRecorder ? (
                <div className="mb-4">
                  <AudioRecorder
                    photoId={photo.photoId}
                    onRecordingComplete={() => setShowAudioRecorder(false)}
                    onCancel={() => setShowAudioRecorder(false)}
                  />
                </div>
              ) : (
                <button
                  onClick={() => setShowAudioRecorder(true)}
                  className="w-full btn-primary py-3 text-sm flex items-center justify-center gap-2 mb-4"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                  </svg>
                  Record Audio Message
                </button>
              )}

              {/* Existing Messages */}
              {messages.length > 0 ? (
                <div className="space-y-3">
                  {messages.map((message: AudioMessage) => (
                    <div key={message.messageId} className="space-y-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">
                          {message.authorName}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400">
                            {Math.floor(message.durationSeconds)}s
                          </span>
                          {(user?.userId === message.authorId || isSuperAdmin) && (
                            <button
                              onClick={() => setDeleteConfirmMessageId(message.messageId)}
                              disabled={deleteAudioMutation.isPending}
                              className="text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                              title="Delete audio message"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                      <audio
                        src={message.audioUrl}
                        controls
                        className="w-full h-8"
                        onPlay={() => handleAudioPlay(message.messageId)}
                        onPause={() => setPlayingAudioId(null)}
                      />
                    </div>
                  ))}
                </div>
              ) : !showAudioRecorder ? (
                <p className="text-xs text-gray-400">No audio messages yet. Be the first!</p>
              ) : null}
            </div>

            {/* Likes List */}
            {likes.length > 0 && (
              <div className="px-3 sm:px-6 py-4 border-b border-gray-100">
                <h3 className="text-[11px] font-bold text-gray-600 uppercase tracking-wider mb-3">
                  ❤️ Liked by
                </h3>
                <div className="space-y-2">
                  {likes.map((like: { userId: string; name: string; email: string }) => (
                    <div key={like.userId} className="flex items-center space-x-2">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary-400 to-accent-400 flex items-center justify-center">
                        <span className="text-xs text-white font-medium">
                          {like.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <span className="text-sm text-gray-700">{like.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Photo Info - Added By & Date */}
            <div className="px-3 sm:px-6 py-4 bg-gray-50 border-t border-gray-200 mt-auto">
              <div className="space-y-2 text-xs">
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-gray-500 uppercase tracking-wide min-w-[80px]">Added by:</span>
                  <span className="text-gray-700 font-medium">{currentPhoto.ownerName}</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-gray-500 uppercase tracking-wide min-w-[80px]">Date Added:</span>
                  <span className="text-gray-700">
                    {new Date(currentPhoto.createdAt).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Audio Confirmation Modal */}
      {deleteConfirmMessageId && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Delete Audio Message
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this audio message? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirmMessageId(null)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  deleteAudioMutation.mutate(deleteConfirmMessageId);
                  setDeleteConfirmMessageId(null);
                }}
                disabled={deleteAudioMutation.isPending}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleteAudioMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingDeleteCommentId && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-gray-900 mb-4">Delete Comment</h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this comment? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setPendingDeleteCommentId(null)}
                className="btn-ghost"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (pendingDeleteCommentId) {
                    deleteCommentMutation.mutate(pendingDeleteCommentId);
                  }
                  setPendingDeleteCommentId(null);
                }}
                disabled={deleteCommentMutation.isPending}
                className="btn-danger"
              >
                {deleteCommentMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Photo Modal */}
      {showShareModal && (
        <SharePhotoModal
          photoId={photo.photoId}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  );
}
